import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";

// Small primitive wrappers so pages get consistent shimmer loading UI
// without every page having to hand-roll its own layout.

export function SkeletonStatCards({ count = 3, testId = "skeleton-stats" }) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-${count} gap-6`} data-testid={testId}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="p-7 border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Skeleton className="h-3 w-24 mb-4" />
              <Skeleton className="h-10 w-20" />
            </div>
            <Skeleton className="w-12 h-12 rounded-md" />
          </div>
        </Card>
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 5, testId = "skeleton-table" }) {
  return (
    <div className="w-full" data-testid={testId}>
      <div className="flex gap-4 px-6 py-3 border-b border-slate-200 dark:border-slate-800">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 px-6 py-4 border-b border-slate-100 dark:border-slate-900 items-center">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={`h-4 flex-1 ${c === 0 ? "max-w-[40%]" : ""}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCardGrid({ count = 6, testId = "skeleton-card-grid" }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={testId}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="p-7 border-slate-200 dark:border-slate-800">
          <div className="flex items-start justify-between mb-5">
            <Skeleton className="w-11 h-11 rounded-md" />
            <div className="text-right">
              <Skeleton className="h-7 w-10 mb-2 ml-auto" />
              <Skeleton className="h-2 w-16 ml-auto" />
            </div>
          </div>
          <Skeleton className="h-5 w-3/4 mb-2" />
          <Skeleton className="h-3 w-1/3" />
          <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800 space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </Card>
      ))}
    </div>
  );
}

export function SkeletonInstitutionProfile() {
  return (
    <div className="w-full max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="skeleton-institution-profile">
      {/* hero */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start mb-16">
        <div className="md:col-span-8">
          <div className="flex items-center gap-5 mb-6">
            <Skeleton className="w-20 h-20 rounded-md" />
            <div className="flex-1">
              <Skeleton className="h-5 w-32 mb-3" />
              <Skeleton className="h-10 w-3/4" />
            </div>
          </div>
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <div className="md:col-span-4">
          <Card className="p-6 border-slate-200 dark:border-slate-800">
            <Skeleton className="h-3 w-24 mb-3" />
            <Skeleton className="h-14 w-24 mb-6" />
            <Skeleton className="h-10 w-full" />
          </Card>
        </div>
      </div>
      {/* body grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <Card className="p-8 md:col-span-5 border-slate-200 dark:border-slate-800 space-y-5">
          <Skeleton className="h-5 w-40 mb-3" />
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="w-4 h-4 mt-1 rounded" />
              <div className="flex-1">
                <Skeleton className="h-2 w-16 mb-1.5" />
                <Skeleton className="h-3 w-40" />
              </div>
            </div>
          ))}
        </Card>
        <div className="md:col-span-7 space-y-8">
          <Card className="p-8 border-slate-200 dark:border-slate-800">
            <Skeleton className="h-5 w-32 mb-6" />
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-40 h-3" />
                  <Skeleton className="flex-1 h-2 rounded-full" />
                  <Skeleton className="w-8 h-4" />
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-8 border-slate-200 dark:border-slate-800">
            <Skeleton className="h-5 w-40 mb-6" />
            <div className="flex items-end gap-3 h-40 pt-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-2">
                  <Skeleton className="h-3 w-6" />
                  <Skeleton className="w-full" style={{ height: `${30 + i * 15}%` }} />
                  <Skeleton className="h-2 w-8" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export function SkeletonInlineStats({ count = 3 }) {
  // Small horizontal stats used in the landing hero (below headline)
  return (
    <div className="flex flex-wrap items-center gap-8" data-testid="skeleton-inline-stats">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <Skeleton className="w-4 h-4 rounded" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}
